import { EventEmitter, Injectable } from "@angular/core";

import { EcgMeasures } from "../models/ecgmeasures";
import { CaliperViewModel } from '../view-models/caliper-viewmodel';
import { ECG_CONSTANTS } from "../utils/ecg-constants";
import { Subject } from 'rxjs';
import { ICustomMeasure } from '../models/iannotation';
import { SeFormulaParser } from '@se/se.ui.advanced';

export class CalibrationOptions {

    // data members

    x: number;
    y: number;

    leadWidth: number;
    leadHeight: number;
    maxVoltage: number;
    milliseconds: number;
    deltaTFactor: number;

    // constructor

    constructor() {

        this.x = 1;
        this.y = 0.1;
        this.leadHeight = ECG_CONSTANTS.LEAD_HEIGHT;
        this.leadWidth = ECG_CONSTANTS.LEAD_WIDTH_25mm;
        this.maxVoltage = ECG_CONSTANTS.MAX_POSITIVE_MICROVOLT_VALUE;
        this.milliseconds = 5000;
        this.deltaTFactor = 1000;
    }
}

export interface ICalculationContext {
    readonly leadWidth: number;
    readonly leadHeight: number;
    readonly columns: number;
    readonly period: number;
}

@Injectable({
    providedIn: 'root',
})
export class EcgMeasuresService {

    measuresChange = new EventEmitter<ICustomMeasure>();

    // constructor

    constructor() {
        // This is intentional
    }

    private _parser: SeFormulaParser = new SeFormulaParser();

    // public functions

    calculate(caliper: CaliperViewModel, calculationContext: ICalculationContext): EcgMeasures {

        let intersection1 = caliper.pointer1.currentIntersection;
        let intersection2 = caliper.pointer2.currentIntersection;
        let calibrationOptions = this.buildCalibrationOptions(calculationContext);
        let seconds = (caliper.pointer1.channel.samplingCount / caliper.pointer1.channel.samplingFrequency) / calculationContext.period;

        let ecgMeasures = new EcgMeasures();
        ecgMeasures.A1 = (calibrationOptions.leadHeight / 2 - intersection1.y) * calibrationOptions.y;
        ecgMeasures.A2 = (calibrationOptions.leadHeight / 2 - intersection2.y) * calibrationOptions.y;
        ecgMeasures.deltaT = seconds * Math.abs(intersection2.x - intersection1.x) * calibrationOptions.deltaTFactor / calculationContext.leadWidth;
        ecgMeasures.deltaA = Math.abs(ecgMeasures.A1 - ecgMeasures.A2);
        ecgMeasures.HR = Math.round(60 / (ecgMeasures.deltaT / 1000));

        return ecgMeasures;
    }

    // private functions

    private buildCalibrationOptions(calculationContext: ICalculationContext) {

        let calibrationOptions = new CalibrationOptions();
        calibrationOptions.leadWidth = calculationContext.leadWidth;
        calibrationOptions.leadHeight = calculationContext.leadHeight;
        calibrationOptions.milliseconds = Math.round(10000 / calculationContext.columns);
        calibrationOptions.x = 1;
        calibrationOptions.y = 0.1;

        return calibrationOptions;
    }


    updateMeasures(measures: ICustomMeasure[]) {
        if (measures) {
            this.assignVariables(measures);
            measures.forEach(measure => {
                if (measure.isCalculated) {
                    this.evaluateFormula(measure);
                    this.measuresChange.emit(measure);
                }
            });
        }
    }

    private assignVariables(measures: Array<ICustomMeasure>) {
        measures.forEach(measure => {
            if (measure.internalUid)
                this._parser.setVariable(measure.internalUid, measure.value);
        });
    }

    private evaluateFormula(measure: ICustomMeasure) {
        measure.value = this.toFixed(this._parser.parse(measure.formula).result, measure.decimalDigit);
    }

    private toFixed(value: number, precision: number) {
        const power = Math.pow(10, precision || 0);
        return Math.round(value * power) / power;
    }

}


@Injectable({
    providedIn: 'root',
})
export class HeartRateUpdaterService {

    //heartRateChange = new EventEmitter<number>();

    heartRateChange = new Subject<number>();

    // constructor

    constructor() {
        // This is intentional
    }

}

